import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { referralAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Copy, Share2, Users, Gift, CheckCircle2, ArrowRight } from 'lucide-react';

const HOW_IT_WORKS = [
  { step: '01', title: 'Share your link', desc: 'Send your unique referral link to anyone who might love MeTag.' },
  { step: '02', title: 'They sign up', desc: 'When they register using your link, they are linked to your account.' },
  { step: '03', title: 'Grow together', desc: 'Track how many people you have brought on board from your dashboard.' },
];

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    referralAPI.getMyReferrals()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load referral data'))
      .finally(() => setLoading(false));
  }, []);

  const referralLink = data ? `${window.location.origin}/register?ref=${data.referralCode}` : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hey! I'm using MeTag — smart NFC business cards that link to your digital profile. Sign up with my link: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="referrals-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Refer & Grow</h1>
        <p className="text-slate-400 text-sm md:text-base">Invite friends and colleagues to join MeTag</p>
      </div>

      <div className="max-w-3xl space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            className="glass rounded-2xl p-5 flex items-center gap-4"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-12 h-12 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-white">{data?.referralCount || 0}</p>
              <p className="text-slate-500 text-xs md:text-sm">Successful Referrals</p>
            </div>
          </motion.div>

          <motion.div
            className="glass rounded-2xl p-5 flex items-center gap-4"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-white">{data?.referralCode || '—'}</p>
              <p className="text-slate-500 text-xs md:text-sm">Your Code</p>
            </div>
          </motion.div>
        </div>

        {/* Referral Link Card */}
        <motion.div
          className="glass rounded-2xl p-6"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        >
          <h2 className="text-white font-semibold mb-1">Your Referral Link</h2>
          <p className="text-slate-500 text-xs mb-5">Share this link — when someone registers through it, they'll be counted as your referral</p>

          {/* Link display */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 bg-slate-800/60 border border-slate-700/50 rounded-xl px-4 py-3 text-slate-300 text-sm font-mono truncate" data-testid="referral-link">
              {referralLink}
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium text-sm transition-all whitespace-nowrap"
              style={{ background: copied ? '#10b981' : 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff' }}
              data-testid="copy-referral-link-btn"
            >
              {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>

          {/* Share buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25"
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-4 h-4" />
              Share on WhatsApp
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 transition-all"
              data-testid="share-copy-btn"
            >
              <Copy className="w-4 h-4" />
              Copy Link
            </button>
          </div>
        </motion.div>

        {/* How it works */}
        <motion.div
          className="glass rounded-2xl p-6"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        >
          <h2 className="text-white font-semibold mb-6">How It Works</h2>
          <div className="space-y-0">
            {HOW_IT_WORKS.map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 rounded-full bg-violet-500/15 border border-violet-500/20 flex items-center justify-center text-xs font-bold text-violet-400 flex-shrink-0">
                    {item.step}
                  </div>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <div className="w-px flex-1 bg-slate-800 my-1 min-h-[1.5rem]" />
                  )}
                </div>
                <div className="pb-6 flex-1">
                  <p className="text-white font-medium text-sm mb-0.5">{item.title}</p>
                  <p className="text-slate-500 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Share prompt */}
        {(data?.referralCount || 0) === 0 && (
          <motion.div
            className="glass rounded-2xl p-5 border border-violet-500/20 flex items-center gap-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          >
            <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center flex-shrink-0">
              <Share2 className="w-5 h-5 text-violet-400" />
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">Be the first to share!</p>
              <p className="text-slate-500 text-xs mt-0.5">No referrals yet — share your link to get started</p>
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-violet-400 hover:text-violet-300 text-sm font-medium transition-colors whitespace-nowrap"
            >
              Copy link <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
